import React, { useState } from 'react';
import RadioWithLabel from 'components/radio-with-label/RadioWithLabel';
import CustomInput, { InputType } from 'components/custom-input.tsx/CustomInput';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { InvoiceByIdV2 } from '../invoices.model';
import { VoidFn } from 'types/baseTypes';
import { If } from 'components';
import { isValidDecimalInput } from 'containers/settings/utils';

enum AmountOption {
  REMAINING = 'remaining',
  OTHER = 'other'
}

interface EditAmountMainSectionProps {
  invoiceData: InvoiceByIdV2;
  onSave: (amount: number, isCustomAmount: boolean) => void;
  onCancel?: VoidFn;
  initialAmount?: number;
  isSaveLoading?: boolean;
}

export const EditAmountMainSection: React.FC<EditAmountMainSectionProps> = ({
  invoiceData,
  onSave,
  onCancel,
  initialAmount,
  isSaveLoading
}) => {
  const remainingAmount = Number(invoiceData?.pendingAmount);

  const isCustomAmount = initialAmount && initialAmount !== remainingAmount;

  const [selectedOption, setSelectedOption] = useState<AmountOption>(
    isCustomAmount ? AmountOption.OTHER : AmountOption.REMAINING
  );
  const [customAmount, setCustomAmount] = useState<string>(isCustomAmount ? initialAmount.toString() : '');

  const amountOptions = [
    {
      id: AmountOption.REMAINING,
      label: (
        <div>
          Remaining amount <span className="font-bold">{getAmountWithCurrency(remainingAmount)}</span>
        </div>
      )
    },
    {
      id: AmountOption.OTHER,
      label: 'Other'
    }
  ];

  const handleSave = () => {
    if (selectedOption === AmountOption.REMAINING) {
      onSave(remainingAmount, false);
    } else {
      const amount = parseFloat(customAmount);
      onSave(amount, true);
    }
  };

  const handleCustomAmountChange = (value: string) => {
    if (isValidDecimalInput(value)) {
      setCustomAmount(value);
    }
  };

  return (
    <div className="flex w-full grow flex-col justify-between gap-6 px-4 py-6">
      <div className="flex flex-col gap-4">
        {amountOptions.map(option => (
          <div key={option.id}>
            <RadioWithLabel
              isSelected={selectedOption === option.id}
              label={option.label}
              onClick={() => setSelectedOption(option.id)}
              containerClassname="text-17px"
            />

            {option.id === AmountOption.OTHER && selectedOption === AmountOption.OTHER && (
              <div className="ml-7 mt-2">
                <CustomInput
                  id="custom-amount-input"
                  type={InputType.TEXT}
                  placeholder="0.00"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  className="h-[48px] w-full pl-7 !text-[#454545] outline-none"
                  prefixWrapperStyle="left-3 !text-[#454545]"
                  prefix="$"
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-auto justify-end space-y-4">
        <CustomButton
          id="record-payment-edit-amount-save-btn"
          onClick={handleSave}
          type={ButtonType.PRIMARY}
          isLoading={isSaveLoading}
          className="w-full">
          Save
        </CustomButton>
        <If condition={!!onCancel}>
          <CustomButton
            id="record-payment-edit-amount-cancel-btn"
            onClick={onCancel}
            type={ButtonType.SECONDARY}
            className="w-full">
            Cancel
          </CustomButton>
        </If>
      </div>
    </div>
  );
};
